import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { IconModule } from '../../modules/icon/icon-module';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../services/notification/notification.service';
import { AvisoService } from '../../services/aviso/aviso.service';
import {
  clearPaginas,
  popPaginaAtiva,
  pushPaginaAtiva,
} from '../../shared/activePage';
import { AuthService } from '../../services/auth/auth.service';

interface MenuSection {
  title: string;
  items: MenuItem[];
}

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  children?: MenuItem[];
  roles?: string[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [IconModule, MatIconModule, CommonModule, RouterModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  unreadAvisosCount = 0;
  unreadNotificationsCount = 0;

  constructor(
    readonly router: Router,
    private notificationService: NotificationService,
    private avisoService: AvisoService,
    private authService: AuthService,
  ) {}

  menu: MenuSection[] = [
    {
      title: 'LINHA DE CUIDADO',
      items: [
        {
          label: 'Empresas',
          icon: '../../../assets/svg/icons/empresas_ic.svg',
          children: [
            {
              label: 'Empresas cadastradas',
              icon: '../../../assets/svg/icons/ponto_ic.svg',
              route: '/empresas',
            },
            {
              label: 'Grupos cadastrados',
              icon: '../../../assets/svg/icons/ponto_ic.svg',
              route: '/grupos',
            },
          ],
        },
        {
          label: 'Pessoas',
          icon: '../../../assets/svg/icons/pessoas_ic.svg',
          children: [
            {
              label: 'Pessoas cadastradas',
              icon: '../../../assets/svg/icons/ponto_ic.svg',
              route: '/pessoas',
            },
            {
              label: 'Importação de pessoas',
              icon: '../../../assets/svg/icons/ponto_ic.svg',
              route: '/pessoas/importar',
            },
            {
              label: 'Exportação de dados',
              icon: '../../../assets/svg/icons/ponto_ic.svg',
              route: '/pessoas/exportar',
              roles: ['ROLE_MASTER', 'ROLE_SAUDE'],
            },
          ],
        },
        {
          label: 'Análise postural',
          icon: '../../../assets/svg/icons/analise_postural_ic.svg',
          route: '/movia/analise-postural',
          roles: ['ROLE_MASTER', 'ROLE_SAUDE'],
        },
      ],
    },
    {
      title: 'GERENCIAMENTO',
      items: [
        {
          label: 'Usuários',
          icon: '../../../assets/svg/icons/cracha_colorido_ic.svg',
          route: '/usuarios',
        },
        {
          label: 'Avisos',
          icon: '../../../assets/svg/icons/avisos_ic.svg',
          route: '/avisos',
        },
        {
          label: 'Gerenciar módulos',
          icon: '../../../assets/svg/icons/modulos.svg',
          route: '/modulos',
          roles: ['ROLE_MASTER'],
        },
      ],
    },
  ];

  isActive(path: string | undefined): boolean {
    return (
      !!path &&
      this.router.isActive(path, {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      })
    );
  }

  podeVer(roles?: string[]): boolean {
    if (!roles || roles.length === 0) {
      return true;
    }

    return roles.some((role) => this.authService.hasRole(role));
  }

  isAnyChildActive(children?: MenuItem[]): boolean {
    return !!children?.some((child) => this.isActive(child.route));
  }

  toggleStates: Record<string, boolean> = {};

  toggle(label: string): void {
    this.toggleStates[label] = !this.toggleStates[label];
  }

  isExpanded(label: string): boolean {
    return !!this.toggleStates[label];
  }

  navegarParaPagina(rota: string, nome: string): void {
    if (rota) {
      this.router.navigate([rota]);
      clearPaginas();
      // pushPaginaAtiva({rota: rota, nome: nome})
    }
  }

  podeVerExportar(): boolean {
    return this.authService.isSaude() || this.authService.isMaster();
  }

  ngOnInit(): void {
    this.notificationService.unreadAvisosCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadAvisosCount = count;
      });

    this.notificationService.unreadNotificacoesCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadNotificationsCount = count;
      });

    this.loadUnreadCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUnreadCount(): void {
    this.avisoService.getUnreadAvisosCount().subscribe({
      next: (response) => {
        this.notificationService.setUnreadAvisosCount(response.count);
      },
      error: (error) => {
        console.error(
          'Erro ao carregar contagem de avisos não visualizados:',
          error,
        );
      },
    });

    this.notificationService.getUnreadNotificacoesCountAPI().subscribe({
      next: (response) => {
        this.notificationService.setUnreadNotificacoesCount(response.count);
      },
      error: (error) => {
        console.error(
          'Erro ao carregar contagem de notificações não visualizadas:',
          error,
        );
      },
    });
  }
}